"use client";
import type { ReactElement } from "react";
import { motion } from "framer-motion";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { FontWeight } from "@/theme/typography";
import { CmarkdownLabelText } from "../Cmarkdowntext/CmarkdownLabelText";
import { SECTION_HEADER_HEIGHT } from "./timeline-constants";
import { selectTimelineSectionColor, selectTimelineSectionFrameModel, } from "./timeline-root-selectors";
import type { TimelineSection } from "./timeline-types";
const CSS_INHERIT = "inherit";
const SECTION_HEADER_GAP = 8;
const SECTION_HEADER_HORIZONTAL_PADDING = 10;
const SECTION_HEADER_FONT_SIZE = 11;
const SECTION_HEADER_FONT_WEIGHT = 600;
const SECTION_HEADER_LETTER_SPACING = "0.06em";
const SECTION_HEADER_FADE_DELAY_SECONDS = 0.3;
const SECTION_HEADER_FADE_DURATION_SECONDS = 0.3;
export interface TimelineSectionHeadersProps {
    chartHeight: number;
    collapsedSections: Set<number>;
    onToggleSection: (sectionIndex: number) => void;
    sections: TimelineSection[];
}
export function TimelineSectionHeaders({ chartHeight, collapsedSections, onToggleSection, sections, }: TimelineSectionHeadersProps): ReactElement {
    return (<>
      {sections.map((section, sectionRenderIndex) => {
            const sectionColor = selectTimelineSectionColor({
                sectionIndex: section.index,
            });
            const isCollapsed = collapsedSections.has(section.index);
            const frameModel = selectTimelineSectionFrameModel({
                chartHeight,
                section,
                sectionCount: sections.length,
                sectionRenderIndex,
            });
            return (<motion.div key={`section-bg-${section.index}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{
                    duration: SECTION_HEADER_FADE_DURATION_SECONDS,
                    delay: SECTION_HEADER_FADE_DELAY_SECONDS,
                    ease: "easeOut",
                }} style={{
                    position: "absolute",
                    left: frameModel.left,
                    top: 0,
                    width: frameModel.width,
                    height: frameModel.height,
                    borderRight: frameModel.borderRight,
                    zIndex: 1,
                }}>
            <Cbutton preset={ButtonPreset.Ghost} ariaLabel={`Toggle ${section.label}`} onClick={() => onToggleSection(section.index)} padding={0} radius={0} disableClickMotion disableHoverEffects style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: SECTION_HEADER_HEIGHT,
                    cursor: "pointer",
                    zIndex: 2,
                }} containerStyle={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "transparent",
                    backgroundImage: "none",
                    color: `${sectionColor}80`,
                    padding: `0 ${SECTION_HEADER_HORIZONTAL_PADDING}px`,
                    fontSize: SECTION_HEADER_FONT_SIZE,
                    fontWeight: SECTION_HEADER_FONT_WEIGHT,
                    letterSpacing: SECTION_HEADER_LETTER_SPACING,
                    textTransform: "uppercase",
                }} contentStyle={{
                    justifyContent: "flex-start",
                    gap: SECTION_HEADER_GAP,
                }}>
              <span>
                {isCollapsed ? "▸" : "▾"}
              </span>
              <CmarkdownLabelText dataElScope="Cmarkdowntimeline/TimelineSectionHeaders" content={section.label} size={SECTION_HEADER_FONT_SIZE} weight={FontWeight.SemiBold} style={{ minWidth: 0, overflow: "hidden" }} paragraphStyle={{ marginBottom: 0, minWidth: 0 }} textStyle={{
                    color: "inherit",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    letterSpacing: CSS_INHERIT,
                    textTransform: CSS_INHERIT,
                }}/>
            </Cbutton>
          </motion.div>);
        })}
    </>);
}
